// AXIOS import
import axios, { AxiosInstance, AxiosResponse } from 'axios';

import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { apiResponseFormatter } from '../utils';
import { encryptionProcess, setAccessTokenLocally, setRefreshTokenForEncryption } from '../store/EncryptionSlice';
import { setModalSignInUp, wipeLocalStorage } from '../store/AppSlice';
import { UserService } from './UserService';
import { modalTypeEnum } from '../types/modal';
import { setModalErrorMessage, setNotification } from '../store/NotificationSlice';
import { env } from '../config/config';

type dispatcher = ThunkDispatch<RootState, null, AnyAction>;

const clientBaseUrl = env.config.swapApiUrl;

export class Api {

    private static dispatch: dispatcher | undefined;
    private static token: string | null;
    private static refreshToken: string | null;
    private static ipaddress: string | null;
    private static errorCount: number = 0;
    private static initialized = false;
    
    // Axios instance
    private static apiInstance: AxiosInstance = axios.create({
        baseURL: clientBaseUrl,
        headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            Pragma: 'no-cache'
        }
    });

    // Set up Axios request interceptor
    private static initializeInterceptors() {

        if (Api.initialized) return;
        Api.initialized = true;

        Api.apiInstance.interceptors.request.use(
            async (config) => {
                
                const url = config?.url || ``;

                if (!url.includes(`quotation`) || !url.includes(`api.ipify.org`)) {

                    if (Api.token && !config?.url?.includes(`refresh`)) {
                        config.headers.Authorization = `Bearer ${Api.token}`;
                    } else if(Api.refreshToken) {
                        config.headers.Authorization = `Bearer ${Api.refreshToken}`;
                    }

                    if (Api.ipaddress) {
                        config.headers['X-Forwarded'] = Api.ipaddress;
                    }
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );

        Api.apiInstance.interceptors.response.use(
            (response: AxiosResponse) => {
                Api.setErrorCounts(0);
                return response;
            },
            async (error) => {

                const originalRequest = error.config;

                if (!Api.dispatch) throw new Error('Missing Dispatch, please use API.setDispatcher');

                if ( error.response?.status === 401 && !originalRequest._retry) {

                    if (!error.request.responseURL.includes('invite')) {
                        const originalRequestUrl = error.request.responseURL;

                        if (originalRequestUrl.includes('refresh')) {
                            window.location.href = '/';
                            //Api.dispatch(wipeLocalStorage());
                            Api.dispatch(setModalSignInUp(modalTypeEnum.register));
                            return false;
                        }

                        const tokensData = await UserService.postRefresh();
                        Api.dispatch(setAccessTokenLocally(tokensData.data.token));
                        Api.setToken(tokensData.data.token);
                        Api.dispatch(setRefreshTokenForEncryption(tokensData.data.refresh_token));
                        Api.setRefreshToken(tokensData.data.refresh_token);
    
                        Api.dispatch(encryptionProcess())
    
                        originalRequest.headers.Authorization = `Bearer ${tokensData.data.token}`;
                        return Api.apiInstance(originalRequest)                        
    
                    } else {
                        Api.dispatch(setModalErrorMessage(error.response.data.message || 'Error Getting Message'));
                        Api.dispatch(setModalSignInUp(modalTypeEnum.unauthorized));
                    }
                } else if (error.response?.status === 403) {
                    Api.dispatch(setModalErrorMessage(error.response.data.message || 'Error Getting Message'));
                    Api.dispatch(setModalSignInUp(modalTypeEnum.unauthorized));
                } else if (error.response?.status === 400) {
                    if (!error.config.data.includes('eth_flow')) {
                        Api.dispatch(setModalErrorMessage(error.response.data.message || 'Error Getting Message'));
                        Api.dispatch(setModalSignInUp(modalTypeEnum.unauthorized));
                    }
                }

                // default return error
                return Promise.reject(error);
            }
        );
    }

    // Dispatchers
    public static setDispatcher = (dispatcher: dispatcher): void => {
        Api.dispatch = dispatcher;
    }
    public static init = (): void => {
        Api.initializeInterceptors();
    }

    // Setup url generator
    public static baseUrlGenerator = async (path: string): Promise<string> => {
        if (path === `client`) return env.config.swapClientApiUrl || ``;
        if (path === `base`) return env.config.swapApiUrl || ``;
        if (path === `price`) return env.config.priceUrl || ``;
        return 'abacate';
    }

    // Setter and custom Setter
    public static setToken = (token: string): void => {
        Api.token = token;
    };
    public static setRefreshToken = (token: string): void => {
        Api.refreshToken = token;
    };
    public static setIpAddress = (ip: string): void => {
        Api.ipaddress = ip;
    };
    public static setErrorCounts = (num : number): void => {
        Api.errorCount = num;
    };

    // GET
    public static get = async <T>(url: string): Promise<T> => {
       return Api.apiInstance.get<T>(url)
        .then(Api.handleSuccess)
        .catch(Api.handleError(`get`));
    }
    // POST
    public static post = async <T>(url: string, obj: any): Promise<T> => {
        return Api.apiInstance.post<T>(url, obj)
            .then(Api.handleSuccess)
            .catch(Api.handleError(`post`));
    }
    // PUT
    public static put = async <T>(url: string, obj: any): Promise<T> => {
       return Api.apiInstance.put<T>(url, obj)
            .then(Api.handleSuccess)
            .catch(Api.handleError('Put'));
    }
    // Patch
    public static patch = <T>(url: string, obj: any): Promise<T> => {
       return Api.apiInstance.patch<T>(Api.formatUrl(url), obj)
            .then(Api.handleSuccess)
            .catch(Api.handleError('Patch'));
    }
    // Delete
    public static delete = <T>(url: string): Promise<T> => {
       return Api.apiInstance.delete<T>(Api.formatUrl(url))
            .then(Api.handleSuccess)
            .catch(Api.handleError('Delete'));
    }

    // Success
    private static handleSuccess = <T>(res: AxiosResponse<T>): T => {
        return apiResponseFormatter(res.data);
    };

    // Error Handler
    private static handleError = (type: string) => async (err: any): Promise<any> => {
        if (!Api.dispatch) throw new Error('Missing Dispatch, please use API.setDispatcher');

        // quote errors: toast only, never a modal (503 from envoy comes without CORS headers, so err.response may be undefined)
        const requestUrl = err?.config?.url || '';
        const isQuoteRequest = requestUrl.includes('quote.trkbit.co') || (!!env.config.priceUrl && requestUrl.startsWith(env.config.priceUrl));
        if (isQuoteRequest) {
            const status = err?.response?.status;
            Api.dispatch(setNotification({
                status: true,
                title: status ? `Erro ${status}` : 'Erro na cotação',
                msg: err?.response?.data?.message || 'Não foi possível obter a cotação. Tente novamente.',
                class: 'error',
                position: 'top'
            }));
            return err;
        }

        if (err?.response) {
            if (err.response.status >= 500) {
                Api.dispatch(setModalErrorMessage(err.response.data.message || 'Error Getting Message'));
                Api.dispatch(setModalSignInUp(modalTypeEnum.serverError));
            } else {
                Api.dispatch(setNotification({
                    status: true,
                    title: `Erro ${err.response.status}`,
                    msg: err.response.data?.message || err.message || 'Error Getting Message',
                    class: 'error',
                    position: 'top'
                }));
            }
        } else {
            Api.dispatch(setModalErrorMessage('Error Getting Message - API error handling'));
            Api.dispatch(setModalSignInUp(modalTypeEnum.serverError));
        }
        return err;
    };

    // format URL
    private static formatUrl = (url: string): string => {
        const indexInterrogation = url.indexOf('?');
        if (indexInterrogation === -1) {
            let formattedUrl = url.replace(/\/$/, '');
            if (!url.includes('image'))
                formattedUrl = formattedUrl + '/';
            return formattedUrl;
        }

        const path = url.substr(0, indexInterrogation).replace(/\/$/, '') + '/';
        return path + url.substr(indexInterrogation);
    };
}